import { CancelIcon } from "@fluentui/react-icons-mdl2"
import { IconButton } from "@fluentui/react/lib/Button"
import { Image, ImageFit } from "@fluentui/react/lib/Image"
import { Modal } from "@fluentui/react/lib/Modal"
import { Stack } from "@fluentui/react/lib/Stack"
import { IProcessedStyleSet, registerIcons } from "@fluentui/react/lib/Styling"
import { Text } from "@fluentui/react/lib/Text"
import { TooltipHost } from "@fluentui/react/lib/Tooltip"
import { classNamesFunction, styled } from "@fluentui/react/lib/Utilities"
import React, { useState } from "react"

import { ProductTitleStyles } from "./ProductTitle.styles"
import { ProductTitleProps, IProductTitleStyles } from "./ProductTitle.types"

const CANCEL = "Cancel"
registerIcons(
    {
        icons: {
            [CANCEL]: <CancelIcon />
        }
    },
    { disableWarnings: true }
)

/**
 * The ProductTitle component renders a product's name, ID and image for a configuration.
 *
 * Links:
 * - [Checkout the code](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/ProductTitle/ProductTitle.tsx)
 * - [ProductTitleStyles](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/ProductTitle/ProductTitle.styles.ts)
 * - [ProductTitleProps](https://gitlab.encoway-services.de/pd/dev/encoway-cpq/-/blob/master/cui/features/application-components/src/components/ProductTitle/ProductTitle.types.ts)
 * - [MS Fluent Stack](https://developer.microsoft.com/de-DE/fluentui#/controls/web/stack)
 * - [MS Fluent Image](https://developer.microsoft.com/de-DE/fluentui#/controls/web/image)
 * - [MS Fluent Tooltip](https://developer.microsoft.com/de-DE/fluentui#/controls/web/tooltip)
 * - [MS Fluent Text](https://developer.microsoft.com/de-DE/fluentui#/controls/web/text)
 * - [MS Fluent Modal](https://developer.microsoft.com/de-DE/fluentui#/controls/web/modal)
 *
 * @visibleName ProductTitle
 */
const IProductTitle = (props: ProductTitleProps) => {
    const [isModalOpen, setModalOpen] = useState(false)

    const imageSrc = props.container?.imageUrl
        ? props.configurationService.mediaLink(
              props.container.imageUrl,
              props.imageResolution || "medium"
          )
        : undefined

    const imageSrcOriginal = props.container?.imageUrl
        ? props.configurationService.mediaLink(props.container.imageUrl)
        : undefined

    const { styles, theme } = props
    const className = classNamesFunction()(
        styles,
        theme
    ) as IProcessedStyleSet<IProductTitleStyles>

    return (
        <>
            <Stack
                className={className.root}
                horizontal
                tokens={{ childrenGap: "1em" }}
            >
                {/* This div is here because Image has no onClick. */}
                <div
                    style={{ cursor: "pointer" }}
                    onClick={() => setModalOpen(true)}
                >
                    {imageSrc ? (
                        <Image
                            imageFit={ImageFit.centerContain}
                            height="3rem"
                            width="3rem"
                            src={imageSrc}
                            className={className.headerImage}
                        />
                    ) : null}
                </div>
                <Stack className={className.text}>
                    <TooltipHost content={props.container.translatedName}>
                        <Text className={className.title} nowrap block>
                            {props.container.translatedName}
                        </Text>
                    </TooltipHost>
                    <TooltipHost content={props.container.name}>
                        <Text className={className.id} nowrap block>
                            {props.container.name}
                        </Text>
                    </TooltipHost>
                </Stack>
            </Stack>
            <Modal
                className={className.modal}
                isOpen={isModalOpen}
                onDismiss={() => setModalOpen(false)}
            >
                <div className={className.modalHeader}>
                    <IconButton
                        iconProps={{ iconName: CANCEL }}
                        onClick={() => setModalOpen(false)}
                    />
                </div>
                <Image
                    className={className.modalImage}
                    src={imageSrcOriginal}
                />
            </Modal>
        </>
    )
}

export const ProductTitle = styled(IProductTitle, ProductTitleStyles)
